import React from "react";
import {
  FileText,
  ShoppingCart,
  TrendingUp,
  BookOpen,
  ArrowRight,
  Beaker,
  FolderOpen,
} from "lucide-react";
import { CatalogItemDto, ProductType } from "../../../api/hooks/useCatalog";
import { JournalEntryDto } from "../../../api/generated/api-client";
import BasicInfoTab from "./tabs/BasicInfoTab/BasicInfoTab";
import PurchaseHistoryTab from "./tabs/PurchaseHistoryTab";
import MarginsTab from "./tabs/MarginsTab/MarginsTab";
import JournalTab from "./tabs/JournalTab";
import UsageTab from "./tabs/UsageTab";
import CompositionTab from "./tabs/CompositionTab";
import MaterialDocumentsTab from "./tabs/MaterialDocumentsTab";
import PifDocumentsTab from "./tabs/PifDocumentsTab";

interface CatalogDetailTabsProps {
  item: CatalogItemDto;
  activeTab: "basic" | "history" | "margins" | "composition" | "journal" | "usage" | "documents" | "pif";
  onTabChange: (
    tab: "basic" | "history" | "margins" | "composition" | "journal" | "usage" | "documents" | "pif",
  ) => void;
  detailData: any;
  isLoading: boolean;
  journalEntries: JournalEntryDto[];
  onManufactureDifficultyClick: () => void;
  onAddJournalEntry: () => void;
  onEditJournalEntry: (entry: JournalEntryDto) => void;
  onViewAllEntries: () => void;
}

const CatalogDetailTabs: React.FC<CatalogDetailTabsProps> = ({
  item,
  activeTab,
  onTabChange,
  detailData,
  isLoading,
  journalEntries,
  onManufactureDifficultyClick,
  onAddJournalEntry,
  onEditJournalEntry,
  onViewAllEntries,
}) => {
  return (
    <div className="flex flex-col overflow-hidden">
      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200 dark:border-graphite-border mb-6">
        <button
          onClick={() => onTabChange("basic")}
          className={`px-4 py-2 text-sm font-medium flex items-center space-x-2 border-b-2 transition-colors ${
            activeTab === "basic"
              ? "border-indigo-500 text-indigo-600 dark:text-graphite-accent dark:border-graphite-accent"
              : "border-transparent text-gray-500 hover:text-gray-700 dark:text-graphite-muted"
          }`}
        >
          <FileText className="h-4 w-4" />
          <span>Základní informace</span>
        </button>

        {/* Historie nákupů - pouze pro Material a Goods */}
        {(item?.type === ProductType.Material ||
          item?.type === ProductType.Goods) && (
          <button
            onClick={() => onTabChange("history")}
            className={`px-4 py-2 text-sm font-medium flex items-center space-x-2 border-b-2 transition-colors ${
              activeTab === "history"
                ? "border-indigo-500 text-indigo-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <ShoppingCart className="h-4 w-4" />
            <span>Historie nákupů</span>
          </button>
        )}

        {(item?.type === ProductType.Product ||
          item?.type === ProductType.SemiProduct ||
          item?.type === ProductType.Goods ||
          item?.type === ProductType.Set) && (
          <button
            onClick={() => onTabChange("margins")}
            className={`px-4 py-2 text-sm font-medium flex items-center space-x-2 border-b-2 transition-colors ${
              activeTab === "margins"
                ? "border-indigo-500 text-indigo-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <TrendingUp className="h-4 w-4" />
            <span>Marže</span>
          </button>
        )}

        {/* Složení tab - pouze pro Product a SemiProduct */}
        {(item?.type === ProductType.Product ||
          item?.type === ProductType.SemiProduct) && (
          <button
            onClick={() => onTabChange("composition")}
            className={`px-4 py-2 text-sm font-medium flex items-center space-x-2 border-b-2 transition-colors ${
              activeTab === "composition"
                ? "border-indigo-500 text-indigo-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <Beaker className="h-4 w-4" />
            <span>Složení</span>
          </button>
        )}

        <button
          onClick={() => onTabChange("journal")}
          className={`px-4 py-2 text-sm font-medium flex items-center space-x-2 border-b-2 transition-colors ${
            activeTab === "journal"
              ? "border-indigo-500 text-indigo-600 dark:text-graphite-accent dark:border-graphite-accent"
              : "border-transparent text-gray-500 hover:text-gray-700 dark:text-graphite-muted"
          }`}
        >
          <BookOpen className="h-4 w-4" />
          <span>Deník</span>
        </button>

        {/* Použití tab - pouze pro SemiProduct a Material */}
        {(item?.type === ProductType.SemiProduct ||
          item?.type === ProductType.Material) && (
          <button
            onClick={() => onTabChange("usage")}
            className={`px-4 py-2 text-sm font-medium flex items-center space-x-2 border-b-2 transition-colors ${
              activeTab === "usage"
                ? "border-indigo-500 text-indigo-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <ArrowRight className="h-4 w-4" />
            <span>Použití</span>
          </button>
        )}

        {/* Dokumenty tab - pouze pro Material */}
        {item?.type === ProductType.Material && (
          <button
            onClick={() => onTabChange("documents")}
            className={`px-4 py-2 text-sm font-medium flex items-center space-x-2 border-b-2 transition-colors ${
              activeTab === "documents"
                ? "border-indigo-500 text-indigo-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <FolderOpen className="h-4 w-4" />
            <span>Dokumenty</span>
          </button>
        )}

        {/* PIF tab - pouze pro Product a SemiProduct */}
        {(item?.type === ProductType.Product ||
          item?.type === ProductType.SemiProduct) && (
          <button
            onClick={() => onTabChange("pif")}
            className={`px-4 py-2 text-sm font-medium flex items-center space-x-2 border-b-2 transition-colors ${
              activeTab === "pif"
                ? "border-indigo-500 text-indigo-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <FolderOpen className="h-4 w-4" />
            <span>PIF</span>
          </button>
        )}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === "basic" ? (
          <BasicInfoTab
            item={item}
            onManufactureDifficultyClick={onManufactureDifficultyClick}
          />
        ) : activeTab === "history" ? (
          <PurchaseHistoryTab
            purchaseHistory={detailData?.historicalData?.purchaseHistory || []}
            isLoading={isLoading}
          />
        ) : activeTab === "margins" ? (
          <MarginsTab
            item={item}
            manufactureCostHistory={
              detailData?.historicalData?.manufactureCostHistory || []
            }
            marginHistory={detailData?.historicalData?.marginHistory || []}
            isLoading={isLoading}
            journalEntries={journalEntries}
          />
        ) : activeTab === "composition" ? (
          <CompositionTab productCode={item.productCode || ""} />
        ) : activeTab === "usage" ? (
          <UsageTab productCode={item.productCode || ""} />
        ) : activeTab === "documents" ? (
          <MaterialDocumentsTab productCode={item.productCode || ""} />
        ) : activeTab === "pif" ? (
          <PifDocumentsTab productCode={item.productCode || ""} />
        ) : (
          <JournalTab
            productCode={item.productCode || ""}
            onAddEntry={onAddJournalEntry}
            onEditEntry={onEditJournalEntry}
            onViewAllEntries={onViewAllEntries}
          />
        )}
      </div>
    </div>
  );
};

export default CatalogDetailTabs;
